/**
 * Decides who shows the price on a product: the theme, or the subscription
 * widget.
 *
 * The starting answer comes from Liquid, which reads the product's selling
 * plans and marks the price block before it ever reaches the browser. This
 * handles what Liquid can't see:
 *
 *   - variant changes, where the shopper moves between a variant the widget
 *     prices and one it doesn't (a Trial Size sold one-time only);
 *   - a product that has selling plans but whose widget never renders, where
 *     the price would otherwise stay hidden with nothing in its place.
 *
 * Everything is a class toggle; the CSS in `subscription-price.liquid` decides
 * what the classes mean.
 */

const SUBSCRIPTION_CLASS = 'price-block--subscription';
const PLAIN_CLASS = 'price-block--plain';
const SECTION_PLAIN_CLASS = 'plain-price-variant';

// Until the page has finished loading, a missing widget means "not yet", not
// "never" — so the price stays hidden and doesn't flash.
let widgetsSettled = false;

/** @returns {string[]} Lower-cased fragments that mark a variant as not-on-subscription. */
function matchList() {
  const configured = document.body.dataset.plainPriceVariants || '';

  return configured
    .split(',')
    .map((fragment) => fragment.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * @param {NodeListOf<Element>} checked - The selected option of every option group.
 * @returns {boolean} Whether the selection's name is one the theme always prices.
 */
function nameIsPlainPriced(checked) {
  const fragments = matchList();

  if (!fragments.length) return false;

  // Every option group, not just the first: on a product with two options the
  // size may be the second picker, and reading only the first one matches
  // against the wrong group entirely. A radio carries its option value as the
  // input's value; a select carries it as the option's text.
  const name = Array.from(checked, (el) => (el instanceof HTMLInputElement ? el.value : el.textContent || ''))
    .join(' ')
    .toLowerCase();

  return fragments.some((fragment) => name.includes(fragment));
}

/**
 * @param {Element} scope - The section the price block lives in.
 * @param {NodeListOf<Element>} checked - The selected option of every option group.
 * @returns {string | undefined} The variant now selected.
 */
function selectedVariantId(scope, checked) {
  // Single-option products carry the variant on the option itself.
  for (const option of checked) {
    if (option instanceof HTMLElement && option.dataset.variantId) return option.dataset.variantId;
  }

  // With more than one option the combination only resolves to a variant in the
  // product form, which the theme keeps up to date.
  const input = scope.querySelector('input[name="id"]');

  return input instanceof HTMLInputElement ? input.value : undefined;
}

/**
 * @param {HTMLElement} price - A price block the widget owns.
 * @param {Element} scope - The section it lives in.
 * @returns {boolean} Whether the theme should be the one showing the price.
 */
function themeShowsPrice(price, scope) {
  // A product with selling plans but no widget on the page: nothing is going to
  // replace this price, so the theme keeps it.
  if (widgetsSettled && !scope.querySelector('.subify-root')) return true;

  const picker = scope.querySelector('variant-picker');
  const checked = picker?.querySelectorAll('input[type="radio"]:checked, option:checked');

  // Nothing to read yet — leave the answer Liquid rendered alone.
  if (!checked?.length) return price.classList.contains(PLAIN_CLASS);

  if (nameIsPlainPriced(checked)) return true;

  const variantId = selectedVariantId(scope, checked);
  const plainVariants = price.dataset.plainVariants;

  if (!variantId || !plainVariants) return price.classList.contains(PLAIN_CLASS);

  return plainVariants.includes(`,${variantId},`);
}

function apply() {
  for (const price of document.querySelectorAll(`product-price.${SUBSCRIPTION_CLASS}`)) {
    if (!(price instanceof HTMLElement)) continue;

    const scope = price.closest('.shopify-section') ?? document.body;
    const plain = themeShowsPrice(price, scope);

    price.classList.toggle(PLAIN_CLASS, plain);
    scope.classList.toggle(SECTION_PLAIN_CLASS, plain);
  }
}

apply();

// The theme re-renders the price and the picker on variant change, and the
// subscription widget renders on its own schedule, so re-check after both.
document.addEventListener('change', apply);
document.addEventListener('variant:selected', apply);
document.addEventListener('variant:update', apply);

// A late-rendering widget or picker would otherwise leave the first paint wrong.
const observer = new MutationObserver(() => apply());
observer.observe(document.documentElement, { childList: true, subtree: true });

function settle() {
  widgetsSettled = true;
  apply();
}

if (document.readyState === 'complete') {
  settle();
} else {
  window.addEventListener('load', settle, { once: true });
}
